import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Stethoscope, UserRound, Home, Heart, MessageCircle,
  Star, MapPin, Clock, ChevronLeft, Search, X,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { providerService, Provider } from '@lib/supabase/provider.service'
import { clsx } from 'clsx'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { queryKeys } from '@lib/queryKeys'
import ServiceAreaSelect from '@components/web/ServiceAreaSelect'
import { matchesServiceArea } from '@lib/constants/nigerianServiceAreas'

// ---- constants ---------------------------------------------------------------
const DERMATOLOGY_ICON = '/dermatology.png'

const SERVICE_META: Record<string, { label: string; desc: string; color: string; icon: any; headerClass: string }> = {
  'Doctor Visit': { label: 'Doctor Visit', desc: 'Comprehensive consultation and in-home medical assessment.', color: '#2563EB', icon: Stethoscope, headerClass: 'bg-blue-600' },
  'Nurse Care': { label: 'Nurse Care', desc: 'Skilled nursing care and ongoing health monitoring.', color: '#059669', icon: UserRound, headerClass: 'bg-emerald-600' },
  'Home Care': { label: 'Home Care', desc: 'Assisted living support and daily care services at home.', color: '#7C3AED', icon: Home, headerClass: 'bg-purple-600' },
  'Specialized Care': { label: 'Specialized Care', desc: 'Specialist-led care tailored to complex health needs.', color: '#DC2626', icon: Heart, headerClass: 'bg-rose-600' },
  Dermatology: { label: 'Dermatology', desc: 'In-home skin, hair, and nail assessment and care.', color: '#0F766E', icon: 'dermatology', headerClass: 'bg-teal-700' },
  'Chat Consultation': { label: 'Chat Consultation', desc: '30-minute real-time provider chat consultation.', color: '#0EA5E9', icon: MessageCircle, headerClass: 'bg-sky-600' },
}

const RATINGS_FILTERS = ['All', 'Highest Rated']

function getInitials(name: string) {
  return name.trim().split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
}

// ---- Provider Card ----------------------------------------------------------
function ProviderCard({ provider, rating, totalReviews, onClick }: {
  provider: Provider
  rating: number
  totalReviews: number
  onClick: () => void
}) {
  const travelRadius = provider.provider_service_areas?.[0]?.travel_radius
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-card border border-border border p-4 hover:border-primary/40 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
          {provider.profile_image_url ? (
            <img src={provider.profile_image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-primary font-bold text-sm">{getInitials(provider.name)}</span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-foreground truncate">{provider.name}</p>
            {provider.is_verified && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                <span>✓ Verified</span>
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{provider.specialty}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{provider.experience || '—'} Years</p>

          {/* Rating + Location + Availability + Price */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Star size={13} className="text-yellow-400 fill-yellow-400" />
              <span className="text-xs font-medium text-foreground">{rating.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">({totalReviews})</span>
            </div>
            {provider.address && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin size={11} />
                <span className="truncate max-w-[150px]">{provider.address}</span>
                {travelRadius && <span>({travelRadius}km)</span>}
              </div>
            )}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock size={11} />
              Available
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          {provider.activeServiceRate?.base_rate != null ? (
            <span className="text-sm font-semibold text-foreground">
              ₦{provider.activeServiceRate.base_rate.toLocaleString()}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
          <ChevronLeft size={18} className="text-muted-foreground group-hover:text-primary transition-colors rotate-180" />
        </div>
      </div>
    </button>
  )
}

// ---- main component ---------------------------------------------------------
export default function ServiceDetail() {
  const navigate = useNavigate()
  const { type } = useParams<{ type: string }>()
  const { patient } = usePatientAuth()
  const decodedType = type ? decodeURIComponent(type) : ''
  const serviceInfo = SERVICE_META[decodedType] || SERVICE_META['Doctor Visit']
  const isChatConsultation = decodedType.toLowerCase() === 'chat consultation'
  const ratingFilters = RATINGS_FILTERS

  const [search, setSearch] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('All')
  const [selectedRating, setSelectedRating] = useState('All')

  const handleBookProvider = useCallback((providerId: string) => {
    navigate(`/patient/book/${providerId}?service=${encodeURIComponent(decodedType)}`)
  }, [decodedType, navigate])

  const providersQuery = useQuery({
    queryKey: queryKeys.patient.serviceProviders(patient?.id, decodedType),
    enabled: !!decodedType,
    queryFn: async () => {
      const raw = await providerService.getProvidersByService(decodedType)
      return Promise.all(
        raw.map(async (provider) => {
          const stats = await providerService.getProviderRatingStats(provider.id)
          return { ...provider, rating: stats.avgRating, totalReviews: stats.totalReviews }
        })
      )
    },
  })

  const providers = providersQuery.data ?? []
  const isLoading = providersQuery.isPending

  const filteredProviders = useMemo(() => providers
    .filter((provider) => {
      // Search filter
      const matchesSearch = !search || 
        provider.name.toLowerCase().includes(search.toLowerCase()) ||
        provider.specialty.toLowerCase().includes(search.toLowerCase())
      if (!matchesSearch) return false

      // Location filter
      if (!isChatConsultation && selectedLocation !== 'All') {
        const hasLocation = provider.provider_service_areas?.some((area) =>
          area.is_active && matchesServiceArea(selectedLocation, area.state)
        )
        if (!hasLocation) return false
      }

      // Rating filter
      if (selectedRating === 'Highest Rated' && provider.rating < 4.0) {
        return false
      }

      return true
    })
    .sort((a, b) => {
      if (selectedRating === 'Highest Rated') {
        return b.rating - a.rating
      }
      return 0
    }), [isChatConsultation, providers, search, selectedLocation, selectedRating])

  return (
    <div className={clsx('min-h-screen flex flex-col', serviceInfo.headerClass)}>
      {/* Header */}
      <div className="text-white px-4 sm:px-6 py-4 sm:py-6">
        <button
          onClick={() => navigate('/patient/dashboard')}
          className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity mb-3 sm:mb-4"
        >
          <ChevronLeft size={20} />
          Back
        </button>
        <div className="flex items-center gap-3">
          {decodedType === 'Dermatology' ? (
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <img src={DERMATOLOGY_ICON} alt="" aria-hidden="true" className="h-6 w-6 object-contain" style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
          ) : null}
          <h1 className="text-2xl sm:text-3xl font-bold">{serviceInfo.label}</h1>
        </div>
        <p className="text-white/80 text-sm sm:text-base mt-1">{serviceInfo.desc}</p>
      </div>

      {/* Content */}
      <div className="flex-1 bg-background rounded-t-2xl mt-4 sm:mt-6 p-4 sm:p-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search providers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
          />
          {search && (
            <button
              title="Clear search"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="space-y-3">
          {/* Location Filter (except Chat Consultation) */}
          {!isChatConsultation && (
            <ServiceAreaSelect
              value={selectedLocation}
              onChange={setSelectedLocation}
            />
          )}

          {/* Rating Filter */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Filter by Rating</p>
            <div className="flex gap-2 flex-wrap">
              {ratingFilters.map((rating) => (
                <button
                  key={rating}
                  onClick={() => setSelectedRating(rating)}
                  className={clsx(
                    'px-3 py-1.5 rounded-full text-sm font-medium border transition-all',
                    selectedRating === rating
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-foreground hover:bg-muted'
                  )}
                >
                  {rating}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div>
          <p className="text-sm font-medium text-foreground mb-3">
            {filteredProviders.length} {filteredProviders.length === 1 ? 'provider' : 'providers'} found
          </p>

          {isLoading ? (
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="text-center py-10 bg-card border border-border rounded-xl">
              <Stethoscope size={32} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm font-medium text-foreground">No providers found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProviders.map((p) => (
                <ProviderCard
                  key={p.id}
                  provider={p}
                  rating={p.rating}
                  totalReviews={p.totalReviews}
                  onClick={() => handleBookProvider(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
